import TextButton from 'components/Luxkit/Button/TextButton'
import Group from 'components/utils/Group'
import Heading from 'components/Luxkit/Typography/Heading'
import React, { FormEventHandler, useCallback, useContext, useEffect, useRef, useState } from 'react'
import BusinessTravellerStepper from 'businessTraveller/components/stepper/BusinessTravellerStepGroup'
import BodyText from 'components/Luxkit/Typography/BodyText'
import PasswordInput from 'components/Common/Form/Input/PasswordInput'
import styled from 'styled-components'
import { useMutation } from '@tanstack/react-query'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { formToObject } from 'lib/forms/formToObject'
import { register } from 'api/auth'
import GeoContext from 'contexts/geoContext'
import { useAppDispatch } from 'hooks/reduxHooks'
import { API_CALL_SUCCESS } from 'actions/actionConstants'
import { USER_REGISTRATION } from 'actions/apiActionConstants'
import {
  BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_ACTIONS,
  BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_STEPS,
  BusinessTravellerBusinessOnboardingDispatchContext,
  BusinessTravellerBusinessOnboardingStateContext,
} from 'businessTraveller/contexts/BusinessTravellerBusinessOnboardingContexts'
import { connect } from 'react-redux'
import { selectLoggedIn } from 'selectors/accountSelectors'
import LayoutContainer from 'components/Common/LayoutContainer'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import IconButton from 'components/Luxkit/Button/IconButton'
import LineArrowLeftIcon from 'components/Luxkit/Icons/line/LineArrowLeftIcon'

const SignupContainer = styled(LayoutContainer)`
  background-color:  ${props => props.theme.palette.neutral.default.eight};
  padding: ${rem(20)};

  ${mediaQueryUp.tablet} {
    padding-top: ${rem(24)};
  }
`

const CreateAccountForm = styled.form`
  position: relative;
`

const stepStatuses: App.BusinessTraveller.BusinessSignupStepKeyStatuses = {
  validate_email: 'complete',
  create_customer: 'active',
  create_business: 'pending',
}

interface MappedStateProps {
  isLoggedIn?: boolean;
}

interface Props {
  steps: App.BusinessTraveller.BusinessSignupSteps
}

function BusinessTravellerSignupCreateCustomer(props: Props & MappedStateProps) {
  const { steps, isLoggedIn } = props
  const [isFormSubmitting, setIsFormSubmitting] = useState<boolean>(false)

  const dispatch = useAppDispatch()
  const { currentRegionCode } = useContext(GeoContext)
  const { adminEmail } = useContext(BusinessTravellerBusinessOnboardingStateContext)
  const accountSetupDispatch = useContext(BusinessTravellerBusinessOnboardingDispatchContext)

  const formRef = useRef<HTMLFormElement>(null)
  const passwordInputRef = useRef<HTMLInputElement>(null)
  const confirmPasswordInputRef = useRef<HTMLInputElement>(null)

  const userRegistrationMutation = useMutation({
    mutationFn: async(userData: App.BusinessTraveller.UserRegistrationData) => {
      return register(userData, currentRegionCode)
    },
    onSuccess: async(data) => {
      dispatch({
        type: API_CALL_SUCCESS,
        api: USER_REGISTRATION,
        data,
        source: 'email',
      })
      setIsFormSubmitting(false)
    },
    onError: (error: App.ApiCallFailure) => {
      passwordInputRef.current?.setCustomValidity(error.message)
      formRef.current?.checkValidity()
      setIsFormSubmitting(false)
    },
  })

  const passwordOnChange = useCallback(() => {
    passwordInputRef.current?.setCustomValidity('')
    passwordInputRef.current?.reportValidity()
  }, [])

  const confirmPasswordOnChange = useCallback(() => {
    confirmPasswordInputRef.current?.setCustomValidity('')
    confirmPasswordInputRef.current?.reportValidity()
  }, [])

  const onBackButtonClick = useCallback(() => {
    accountSetupDispatch({
      type: BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_ACTIONS.STEP_INTO,
      step: BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_STEPS.VALIDATE_EMAIL,
    })
  }, [accountSetupDispatch])

  const handleSubmit = useCallback<FormEventHandler<HTMLFormElement>>((e) => {
    e.preventDefault()
    const formData = formToObject<App.BusinessTraveller.UserRegistrationData>(e.currentTarget)

    if (formData.password !== formData.confirmPassword) {
      confirmPasswordInputRef.current?.setCustomValidity('Passwords do not match. Please ensure both fields are the same.')
      confirmPasswordInputRef.current?.reportValidity()
    } else {
      setIsFormSubmitting(true)
      userRegistrationMutation.mutate({
        ...formData,
        email: adminEmail,
      })
    }
  }, [adminEmail, userRegistrationMutation])

  useEffect(() => {
    if (isLoggedIn) {
      accountSetupDispatch({
        type: BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_ACTIONS.STEP_INTO,
        step: BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_STEPS.CREATE_BUSINESS,
      })
    }
  }, [accountSetupDispatch, isLoggedIn])

  return (
    <SignupContainer>
      <CreateAccountForm onSubmit={handleSubmit} ref={formRef}>
        {isFormSubmitting && <LoadingIndicator floating />}
        <Group direction="vertical" gap={20}>
          <Group direction="horizontal" verticalAlign="center">
            <IconButton
              kind="tertiary"
              variant="dark"
              onClick={onBackButtonClick}
              horizontalOutdent="start"
            >
              <LineArrowLeftIcon />
            </IconButton>
            <Heading variant="heading3">Create your password</Heading>
          </Group>
          <BusinessTravellerStepper steps={steps} stepStatuses={stepStatuses} />
          <BodyText variant="medium">Create your password for {adminEmail}</BodyText>
          <Group direction="vertical" gap={8}>
            <PasswordInput
              autoComplete="new-password"
              label="Password"
              minLength={8}
              name="password"
              onChange={passwordOnChange}
              ref={passwordInputRef}
              required
              requiredErrorMessage="Please enter a password"
            />
            <PasswordInput
              autoComplete="new-password"
              label="Confirm password"
              minLength={8}
              name="confirmPassword"
              onChange={confirmPasswordOnChange}
              ref={confirmPasswordInputRef}
              required
              requiredErrorMessage="Please confirm password"
            />
            <Group direction="horizontal" horizontalAlign="end">
              <TextButton
                fit="mobile-full-width"
                kind="primary"
                shape="square"
                size="medium"
                type="submit"
                disabled={isFormSubmitting}
              >
                Continue
              </TextButton>
            </Group>
          </Group>
        </Group>
      </CreateAccountForm>
    </SignupContainer>
  )
}

export default connect<MappedStateProps, undefined, {}, App.State>((state) => {
  return { isLoggedIn: selectLoggedIn(state) }
})(BusinessTravellerSignupCreateCustomer)
